import { useRef, useState } from "react";
import { Loader2, SlidersHorizontal, Square, X } from "lucide-react";
import { toast } from "sonner";
import { Chip } from "@/components/hte/HteForm";
import { errMsg, isCancel, runJob, useApi } from "@/lib/api";
import { LAYERS } from "@/lib/hte";

const CHIPS = {
  q: ["Tambah ayat berkaitan", "Fokus pada sudut kepimpinan", "Utamakan pandangan Ibn Asyur"],
  v: ["Tekankan nilai amanah", "Jadikan prinsip lebih praktikal", "Fokus budaya keluarga"],
  k: ["Tambah ilmu ekonomi", "Kemahiran untuk fasilitator", "Sistem yang guna AI"],
  i: ["Inovasi untuk rumah ngaji", "Bentuk aplikasi mudah alih", "Kos rendah, boleh mula esok"],
};

const hint = (key) => {
  const after = LAYERS.slice(LAYERS.findIndex((l) => l.key === key) + 1).map((l) => l.letter);
  return after.length ? `Lapis ${after.join(", ")} akan diselaraskan supaya kekal sejalan.` : "Amanah 7 hari akan diselaraskan jika perlu.";
};

export const TweakBox = ({ layerKey, kajianId, onUpdated, disabled, setBusy }) => {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const ctrl = useRef(null);
  const run = async () => {
    ctrl.current = new AbortController();
    setRunning(true); setBusy(true);
    try {
      const k = await runJob(api, `/hte/kajian/${kajianId}/tweak`, { layer: layerKey, instruction: text.trim() }, ctrl.current.signal);
      onUpdated(k); setOpen(false); setText("");
      toast.success("Lapis dilaraskan · versi baharu disimpan");
    } catch (e) { if (!isCancel(e)) toast.error(errMsg(e)); } finally { setRunning(false); setBusy(false); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} disabled={disabled} data-testid={`tweak-open-${layerKey}`}
      className={`text-sm inline-flex items-center gap-1.5 text-${layerKey} underline-offset-4 hover:underline disabled:opacity-40`}>
      <SlidersHorizontal size={14} /> Ubah lapis ini
    </button>
  );
  return (
    <div className="rounded-xl border border-line bg-surface p-4 space-y-3 fade-up" data-testid={`tweak-panel-${layerKey}`}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">Arahan untuk lapis {layerKey.toUpperCase()}</span>
        {!running && <button onClick={() => setOpen(false)} aria-label="Tutup" data-testid={`tweak-close-${layerKey}`} className="text-mute hover:text-ink"><X size={16} /></button>}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} disabled={running} data-testid={`tweak-input-${layerKey}`}
        placeholder="Contoh: fokus pada pasukan kecil di sekolah"
        className="w-full px-3 py-2 rounded-lg bg-bg border border-line text-sm focus:outline-none focus:border-[var(--q)] resize-y" />
      <div className="flex flex-wrap gap-2">
        {CHIPS[layerKey].map((c, i) => <Chip key={c} onClick={() => setText(c)} testid={`tweak-chip-${layerKey}-${i}`} active={text === c}>{c}</Chip>)}
      </div>
      <p className="text-xs text-mute" data-testid={`tweak-hint-${layerKey}`}>{hint(layerKey)}</p>
      {running ? (
        <div className="flex items-center gap-3">
          <span className="text-sm text-mute inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin text-q" /> Melaras lapis…</span>
          <button key="stop" onClick={() => ctrl.current?.abort()} data-testid={`tweak-cancel-${layerKey}`}
            className="h-9 px-4 rounded-full border border-line text-sm inline-flex items-center gap-1.5"><Square size={12} /> Henti</button>
        </div>
      ) : (
        <button key="go" onClick={run} disabled={text.trim().length < 2} data-testid={`tweak-submit-${layerKey}`}
          className="h-10 px-5 rounded-full bg-ink text-bg text-sm font-semibold disabled:opacity-40">Laraskan</button>
      )}
    </div>
  );
};
